import { Card } from '@/components/ui/card';
import { Users, FileText, TrendingUp, CheckCircle, Briefcase, MapPin } from 'lucide-react';

// Reliable-today Admin metrics ONLY (computed from current authoritative collections).
// Regional seeker retention / conversion funnels are intentionally NOT shown — they
// require the activity_events history we have only just begun collecting (see Prompt 3).
const KPI = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="p-5 border border-slate-200 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className="text-3xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <Icon className={`w-9 h-9 ${color || 'text-brand'}`} strokeWidth={1.5} />
    </div>
  </Card>
);

export default function AdminOverview({ analytics }) {
  if (!analytics) {
    return (
      <Card className="p-8 border border-slate-200 text-center" data-testid="admin-overview-empty">
        <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">Metrics are not available right now.</p>
      </Card>
    );
  }
  const u = analytics.users || {};
  const r = analytics.resumes || {};
  const a = analytics.applications || {};
  const j = analytics.jobs || {};
  const e = analytics.employers || {};
  const completePct = r.total ? Math.round((r.complete / r.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Platform</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPI icon={Users} label="Total Users" value={u.total ?? 0} />
          <KPI icon={Users} label="Active Job Seekers" value={u.job_seekers ?? 0} color="text-emerald-600" />
          <KPI icon={FileText} label="Resume Complete" value={`${completePct}%`} sub={`${r.complete ?? 0} of ${r.total ?? 0}`} color="text-sky-600" />
          <KPI icon={TrendingUp} label="Applications In Motion" value={a.in_motion ?? 0} sub={`${a.total ?? 0} total`} color="text-amber-600" />
          <KPI icon={CheckCircle} label="Hires" value={a.hired ?? 0} color="text-emerald-600" />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 mb-3">Employers &amp; Opportunities</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPI icon={Briefcase} label="Approved Employers" value={e.approved ?? 0} sub={`${e.pending ?? 0} pending`} color="text-purple-600" />
          <KPI icon={Briefcase} label="Active Opportunities" value={j.active ?? 0} sub={`${j.total ?? 0} total`} color="text-brand" />
          <KPI icon={MapPin} label="Active Regions" value={analytics.regions?.active ?? 0} sub={`${analytics.regions?.total ?? 0} total`} color="text-purple-600" />
          <KPI icon={Users} label="Regional Admins" value={u.regional_admins ?? 0} color="text-slate-600" />
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Funnel conversion and regional seeker analytics will populate once activity tracking has accumulated history.
      </p>
    </div>
  );
}
